import React, { useState, useEffect } from "react";
import {
  Spin,
  Row,
  Col,
  Card,
  Table,
  Progress,
  Collapse,
  Select,
  DatePicker,
  message,
  Tag,
  Statistic,
} from "antd";
import {
  request,
  formatter,
  formatSnakeCase,
  getNumericValue,
  getStateColor,
  unitFormatter,
} from "../helpers/utilities";
import moment from "moment";
import { AxiosResponse } from "axios";
import * as Sentry from "@sentry/browser";
import {
  CompanyWithCustomerCompanyAssociationId,
  CustomerBalanceV2,
} from "../helpers/shared-interfaces";
import { GET_CUSTOMER_BALANCE_V2 } from "../gql/customers";
import { NavLink } from "react-router-dom";
import { generateDisplayErrorMessage } from "../helpers/utilities";

import { InfoCircleOutlined, LockFilled } from "@ant-design/icons";

const { Panel } = Collapse;
const { Option } = Select;

interface BalanceCardProps {
  title: string | React.ReactNode;
  value: number;
  color: string;
  prefix?: string;
}

const BalanceCard: React.FC<BalanceCardProps> = ({ title, value, color, prefix = "GHS" }) => (
  <Card
    style={{
      borderLeft: `4px solid ${color}`,
      borderRadius: 8,
      height: "100%",
    }}
    bodyStyle={{ padding: 20, display: "flex", flexDirection: "column", minHeight: 120 }}
  >
    <Statistic
      title={title}
      value={value}
      precision={2}
      prefix={prefix}
      valueStyle={{ fontSize: 24, fontWeight: 600, color }}
    />
  </Card>
);

interface CustomerBalanceBreakdownProps {
  customerPetraId: string;
  fundCode: string;
  clientPetraId?: string;
  priceDate?: string;
  // Optional props for modal/extended mode
  companies?: CompanyWithCustomerCompanyAssociationId[];
  showDatePicker?: boolean;
  showExtendedColumns?: boolean;
  showTitle?: boolean;
  showPendingRedemptionsCard?: boolean;
  compact?: boolean;
}

const CustomerBalanceBreakdown: React.FC<CustomerBalanceBreakdownProps> = ({
  customerPetraId,
  fundCode,
  clientPetraId: initialClientPetraId,
  priceDate: initialPriceDate,
  companies,
  showDatePicker = false,
  showExtendedColumns = false,
  showTitle = true,
  showPendingRedemptionsCard = true,
  compact = true,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [balanceData, setBalanceData] = useState<CustomerBalanceV2 | null>(
    null,
  );
  const [selectedCompany, setSelectedCompany] = useState<string>(
    initialClientPetraId || (companies && companies.length > 0 ? companies[0].petraId : ""),
  );
  const [selectedPriceDate, setSelectedPriceDate] =
    useState<moment.Moment | null>(
      initialPriceDate ? moment(initialPriceDate) : moment(),
    );

  // Auto-select first company as default
  useEffect(() => {
    if (companies && companies.length > 0 && !selectedCompany) {
      setSelectedCompany(companies[0].petraId);
    }
  }, [companies, selectedCompany]);

  // Update selected company when prop changes
  useEffect(() => {
    if (initialClientPetraId) {
      setSelectedCompany(initialClientPetraId);
    } else if (companies && companies.length > 0 && !selectedCompany) {
      setSelectedCompany(companies[0].petraId);
    }
  }, [initialClientPetraId, companies, selectedCompany]);

  // Update selected date when prop changes
  useEffect(() => {
    if (initialPriceDate) {
      setSelectedPriceDate(moment(initialPriceDate));
    }
  }, [initialPriceDate]);

  useEffect(() => {
    if (customerPetraId && fundCode && selectedCompany) {
      fetchBalanceData();
    }
  }, [customerPetraId, fundCode, selectedCompany, selectedPriceDate]);

  const fetchBalanceData = async () => {
    if (!customerPetraId || !fundCode || !selectedCompany) return;

    setLoading(true);
    try {
      const response: AxiosResponse = await request({
        query: GET_CUSTOMER_BALANCE_V2,
        variables: {
          customerPetraId,
          fundCode,
          clientPetraId: selectedCompany,
          priceDate: selectedPriceDate
            ? selectedPriceDate.format("YYYY-MM-DD")
            : moment().format("YYYY-MM-DD"),
        },
      });

      if (response.data.data.customerBalanceV2) {
        setBalanceData(response.data.data.customerBalanceV2);
      } else {
        message.error("No balance data found");
      }
    } catch (error) {
      message.error(`Failed to fetch balance details - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (value: string) => {
    setSelectedCompany(value);
  };

  const handlePriceDateChange = (date: moment.Moment | null) => {
    setSelectedPriceDate(date);
  };

  const allocationTypeMap: Record<string, string> = {
    employee_pre_tax: "Employee Pre Tax",
    employee_post_tax: "Employee Post Tax",
    employer_pre_tax: "Employer Pre Tax",
    employer_post_tax: "Employer Post Tax",
    contribution: "Contribution",
  };

  const prepareTableData = (): any[] => {
    if (!balanceData || !balanceData.allocations) return [];

    const rows: any[] = [];
    const allocations = balanceData.allocations;
    const pendingDeductions = balanceData.pendingRedemptionDeductions || {};
    const liens = balanceData.lienDeductions || {};
    const redeemable = balanceData.redeemableAllocations || {};
    const vestedAllocations = balanceData.vestedAllocations || {};
    const unvestedAllocations = balanceData.unvestedAllocations || {};

    const applicableAllocations = balanceData.applicableAllocations;

    Object.keys(allocations)
      .filter((key) => !applicableAllocations || applicableAllocations.includes(key))
      .forEach((key) => {
      const allocation = allocations[key];

      const contributions = getNumericValue(allocation.contributions);
      const redemptions = getNumericValue(allocation.redemptions);
      const amount = getNumericValue(allocation.amount);
      const units = getNumericValue(allocation.units);
      const interest = getNumericValue(allocation.interest);
      const balance = getNumericValue(allocation.balance);
      const vestedBalance = getNumericValue(vestedAllocations[key]?.balance);
      const unvestedBalance = getNumericValue(
        unvestedAllocations[key]?.balance,
      );
      const pending = getNumericValue(pendingDeductions[key]?.amount);
      const lien = getNumericValue(liens[key]);
      const redeemableAmount = getNumericValue(redeemable[key]);

      const isVestingAdjusted =
        balanceData.vestingComputations &&
        balanceData.vestingComputations.vested_percentage < 100 &&
        (key === "employer_pre_tax" || key === "employer_post_tax");

      const isTier2Contribution =
        key === "contribution" &&
        balanceData.productTier === "tier_2" &&
        applicableAllocations?.includes("contribution");

      rows.push({
        type: key,
        displayName: isTier2Contribution
          ? "Employee Pre-Tax (Tier 2)"
          : allocationTypeMap[key] || key,
        contributions,
        redemptions,
        amount,
        units,
        interest,
        balance,
        vestedBalance,
        unvestedBalance,
        pending,
        liens: lien,
        redeemable: redeemableAmount,
        vestingAdjusted: isVestingAdjusted,
        isTotal: false,
      });
    });

    if (balanceData.totalAllocations) {
      rows.push({
        type: "totals",
        displayName: "Totals",
        contributions: getNumericValue(
          balanceData.totalAllocations.total_contributions,
        ),
        redemptions: getNumericValue(
          balanceData.totalAllocations.total_redemptions,
        ),
        amount: getNumericValue(balanceData.totalAllocations.total_amount),
        units: getNumericValue(balanceData.totalAllocations.total_units),
        interest: getNumericValue(balanceData.totalAllocations.total_interest),
        balance: getNumericValue(balanceData.totalAllocations.total_balance),
        vestedBalance: getNumericValue(
          balanceData.totalVestedAllocations?.total_balance,
        ),
        pending: getNumericValue(
          balanceData.totalPendingRedemptionDeductions?.total_amount,
        ),
        liens: getNumericValue(balanceData.totalLienDeductions),
        redeemable: getNumericValue(balanceData.totalRedeemableAllocations),
        unvestedBalance: getNumericValue(
          balanceData.totalUnvestedAllocations?.total_balance,
        ),
        isTotal: true,
      });
    }

    return rows;
  };

  const formatAmount = (value: number): string => {
    return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const getColorForAllocationType = (type: string): string => {
    const colorMap: Record<string, string> = {
      employee_pre_tax: "#1890ff",
      employee_post_tax: "#52c41a",
      employer_pre_tax: "#722ed1",
      employer_post_tax: "#fa8c16",
      contribution: "#13c2c2",
    };
    return colorMap[type] || "#d9d9d9";
  };

  const baseColumns = [
    {
      title: "Allocation Type",
      dataIndex: "displayName",
      key: "displayName",
      render: (text: string, record: any) => (
        <div style={{ whiteSpace: "nowrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {!record.isTotal && (
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: getColorForAllocationType(record.type),
                  display: "inline-block",
                }}
              />
            )}
            <span style={{ fontWeight: record.isTotal ? 600 : 500 }}>
              {text}
            </span>
          </div>
          {record.vestingAdjusted && (
            <Tag
              color="green"
              style={{ marginTop: 4, fontSize: 10, padding: "0 4px" }}
            >
              vesting-adjusted
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Contributions (GHS)",
      dataIndex: "contributions",
      key: "contributions",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span style={{ fontWeight: record.isTotal ? 600 : 400 }}>
          {formatAmount(value)}
        </span>
      ),
    },
    {
      title: "Redemptions (GHS)",
      dataIndex: "redemptions",
      key: "redemptions",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span
          style={{
            color: value > 0 ? "#ff4d4f" : undefined,
            fontWeight: record.isTotal ? 600 : 400,
          }}
        >
          {formatAmount(value)}
        </span>
      ),
    },
  ];

  const extendedColumns = [
    {
      title: "Net Amount (GHS)",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span
          style={{
            color: value < 0 ? "#ff4d4f" : undefined,
            fontWeight: record.isTotal ? 600 : 400,
          }}
        >
          {formatAmount(value)}
        </span>
      ),
    },
    {
      title: "Units",
      dataIndex: "units",
      key: "units",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span style={{ fontWeight: record.isTotal ? 600 : 400 }}>
          {value.toFixed(4)}
        </span>
      ),
    },
    {
      title: "Interest (GHS)",
      dataIndex: "interest",
      key: "interest",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span
          style={{
            color: value > 0 ? "#52c41a" : value < 0 ? "#ff4d4f" : undefined,
            fontWeight: record.isTotal ? 600 : 400,
          }}
        >
          {formatAmount(value)}
        </span>
      ),
    },
  ];

  const remainingColumns = [
    {
      title: "Balance (GHS)",
      dataIndex: "balance",
      key: "balance",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span style={{ fontWeight: record.isTotal ? 600 : 400 }}>
          {formatAmount(value)}
        </span>
      ),
    },
    {
      title: (
        <div>
          <div>
            <LockFilled style={{ fontSize: 10, marginRight: 4, color: "#8c8c8c" }} />
            <span style={{ color: "#8c8c8c" }}>Unvested (GHS)</span>
          </div>
          <div style={{ color: "#52c41a" }}>Vested (GHS)</div>
        </div>
      ),
      dataIndex: "vestedBalance",
      key: "vestedBalance",
      align: "right" as const,
      render: (value: number, record: any) => {
        return (
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                color: "#8c8c8c",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 4,
                fontWeight: record.isTotal ? 600 : 400,
              }}
            >
              <LockFilled style={{ fontSize: 10 }} />
              <span>{formatAmount(record.unvestedBalance)}</span>
            </div>
            <div
              style={{
                fontWeight: record.isTotal ? 600 : 500,
                color: "#52c41a",
              }}
            >
              {formatAmount(value)}
            </div>
          </div>
        );
      },
    },
    {
      title: "Pending (GHS)",
      dataIndex: "pending",
      key: "pending",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span
          style={{
            color: value > 0 ? "#ff4d4f" : undefined,
            fontWeight: record.isTotal ? 600 : 400,
          }}
        >
          {formatAmount(value)}
        </span>
      ),
    },
    {
      title: "Lien (GHS)",
      dataIndex: "liens",
      key: "liens",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span
          style={{
            color: value > 0 ? "#ff4d4f" : undefined,
            fontWeight: record.isTotal ? 600 : 400,
          }}
        >
          {formatAmount(value)}
        </span>
      ),
    },
    {
      title: "Redeemable (GHS)",
      dataIndex: "redeemable",
      key: "redeemable",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span style={{ fontWeight: 600 }}>{formatAmount(value)}</span>
      ),
    },
  ];

  const columns = showExtendedColumns
    ? [...baseColumns, ...extendedColumns, ...remainingColumns]
    : [...baseColumns, ...remainingColumns];

  const pendingWithdrawalsColumns = [
    {
      title: "Request ID",
      dataIndex: "id",
      key: "id",
      render: (id: string) => (
        <NavLink to={`/redemption-requests/${id}`}>
          <span style={{ fontWeight: 500, marginRight: 8 }}>{id}</span>
        </NavLink>
      ),
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render: (state: string) => (
        <Tag
          color={getStateColor(state)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(state)}
        </Tag>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amountPaid",
      key: "amountPaid",
      align: "right" as const,
      render: (amountPaid: any) => (
        <span style={{ fontWeight: 600 }}>
          {formatter.format(getNumericValue(amountPaid))}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "requestDate",
      key: "requestDate",
      align: "right" as const,
      render: (requestDate: string) =>
        requestDate ? moment(requestDate).format("YYYY-MM-DD") : "N/A",
    },
  ];

  const customerTransactionsColumns = [
    {
      title: "Transaction ID",
      dataIndex: "id",
      key: "id",
      render: (id: string) => (
        <NavLink to={`/online-transactions/${id}`}>
          <span style={{ fontWeight: 500, marginRight: 8 }}>{id}</span>
        </NavLink>
      ),
    },
    {
      title: "Type",
      dataIndex: "transactionType",
      key: "transactionType",
      render: (transactionType: string) => (
        <span style={{ textTransform: "capitalize" }}>
          {formatSnakeCase(transactionType)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag
          color={getStateColor(status)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(status)}
        </Tag>
      ),
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right" as const,
      render: (totalAmount: any) => (
        <span style={{ fontWeight: 600 }}>
          {formatter.format(getNumericValue(totalAmount))}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "right" as const,
      render: (createdAt: string) =>
        createdAt ? moment(createdAt).format("YYYY-MM-DD") : "N/A",
    },
  ];

  const renderPendingWithdrawals = () => {
    if (
      !balanceData?.pendingRedemptionRequests ||
      !balanceData.pendingRedemptionRequests.length
    ) {
      return null;
    }

    return (
      <Collapse
        ghost
        style={{ marginTop: 24 }}
        defaultActiveKey={["pending_withdrawals"]}
      >
        <Panel
          header={
            <span style={{ fontWeight: 500 }}>
              Pending Redemptions (
              {balanceData.pendingRedemptionRequests.length})
            </span>
          }
          key="pending_withdrawals"
        >
          <Table
            columns={pendingWithdrawalsColumns}
            dataSource={balanceData.pendingRedemptionRequests}
            pagination={false}
            size="small"
            rowKey="id"
            scroll={{ x: 500 }}
          />
        </Panel>
      </Collapse>
    );
  };

  const renderCustomerTransactions = () => {
    if (
      !balanceData?.pendingCustomerTransactions ||
      !balanceData.pendingCustomerTransactions.length
    ) {
      return null;
    }

    return (
      <Collapse ghost style={{ marginTop: 24 }}>
        <Panel
          header={
            <span style={{ fontWeight: 500 }}>
              Pending Customer Transactions (
              {balanceData.pendingCustomerTransactions.length})
            </span>
          }
          key="customer_transactions"
        >
          <Table
            columns={customerTransactionsColumns}
            dataSource={balanceData.pendingCustomerTransactions}
            pagination={false}
            size="small"
            rowKey="id"
          />
        </Panel>
      </Collapse>
    );
  };

  const hasCompanySelector = companies && companies.length >= 1;
  const showSelectors = hasCompanySelector || showDatePicker;

  if (!customerPetraId || !fundCode || (!selectedCompany && !companies)) {
    return null;
  }

  return (
    <Spin spinning={loading}>
      <div style={{ padding: "16px 0" }}>
        {showTitle && (
          <h2 style={{ marginBottom: 16 }}>Customer Balance Information</h2>
        )}

        {/* Company and Date Selectors */}
        {showSelectors && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            {hasCompanySelector && (
              <Col span={8}>
                <label
                  style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
                >
                  Select Company
                </label>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select a company"
                  value={selectedCompany || undefined}
                  onChange={handleCompanyChange}
                >
                  {companies!.map((company) => (
                    <Option key={company.petraId} value={company.petraId}>
                      {company.name}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            {showDatePicker && (
              <Col span={8}>
                <label
                  style={{ display: "block", marginBottom: 8, fontWeight: 500 }}
                >
                  Price Date
                </label>
                <DatePicker
                  style={{ width: "100%" }}
                  value={selectedPriceDate}
                  onChange={handlePriceDateChange}
                  format="YYYY-MM-DD"
                  allowClear={false}
                />
              </Col>
            )}
          </Row>
        )}

        {balanceData && (
          <>
            {/* Summary Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24, display: "flex", flexWrap: "wrap" }}>
              <Col style={{ flex: "1 1 160px", minWidth: 160 }}>
                <BalanceCard
                  title="Total Balance"
                  value={getNumericValue(
                    balanceData.totalAllocations?.total_balance,
                  )}
                  color="#667eea"
                />
              </Col>
              <Col style={{ flex: "1 1 160px", minWidth: 160 }}>
                <BalanceCard
                  title="Redeemable Balance"
                  value={getNumericValue(
                    balanceData.totalRedeemableAllocations,
                  )}
                  color="#1890ff"
                />
              </Col>
              <Col style={{ flex: "1 1 160px", minWidth: 160 }}>
                <BalanceCard
                  title={
                    <span>
                      <LockFilled style={{ fontSize: 12, marginRight: 4, color: "#8c8c8c" }} />
                      Unvested Balance
                    </span>
                  }
                  value={getNumericValue(
                    balanceData.totalUnvestedAllocations?.total_balance,
                  )}
                  color="#8c8c8c"
                />
              </Col>
              <Col style={{ flex: "1 1 160px", minWidth: 160 }}>
                <BalanceCard
                  title="Vested Balance"
                  value={getNumericValue(
                    balanceData.totalVestedAllocations?.total_balance,
                  )}
                  color="#52c41a"
                />
              </Col>
              {showPendingRedemptionsCard && (
                <Col style={{ flex: "1 1 160px", minWidth: 160 }}>
                  <BalanceCard
                    title="Pending Redemptions"
                    value={getNumericValue(
                      balanceData.totalPendingRedemptionDeductions?.total_amount,
                    )}
                    color="#fa8c16"
                  />
                </Col>
              )}
            </Row>

            {/* Metadata Row */}
            <Row
              gutter={16}
              style={{
                marginBottom: 24,
                padding: "12px 16px",
                background: "#f5f5f5",
                borderRadius: 8,
              }}
            >
              <Col span={8}>
                <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                  Current Price:{" "}
                </span>
                <span style={{ fontWeight: 500 }}>
                  GHS {balanceData.price ? unitFormatter.format(balanceData.price): "N/A"}
                </span>
                <span style={{ fontSize: 12, marginLeft: 8, color: "#8c8c8c" }}>
                  ({moment(balanceData.priceDate).format("YYYY-MM-DD")})
                </span>
              </Col>
              <Col span={8}>
                <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                  Total Lien:{" "}
                </span>
                <span style={{ fontWeight: 500 }}>
                  GHS{" "}
                  {formatAmount(
                    getNumericValue(balanceData.totalLienDeductions),
                  )}
                </span>
              </Col>
              {balanceData.productName && (
                <Col span={8}>
                  <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                    Product:{" "}
                  </span>
                  <span style={{ fontWeight: 500 }}>
                    {balanceData.productName}
                  </span>
                </Col>
              )}
            </Row>

            {/* Allocation Table */}
            <Table
              columns={columns}
              dataSource={prepareTableData()}
              pagination={false}
              rowKey="type"
              bordered
              size={compact ? "small" : undefined}
              scroll={{ x: "max-content" }}
              style={{ marginBottom: 24 }}
              rowClassName={(record) => (record.isTotal ? "totals-row" : "")}
            />

            {/* Vesting Status */}
            {balanceData.vestingComputations &&
              balanceData.vestingComputations.vested_percentage !== null && (
                <Card
                  style={{ marginBottom: 24, background: "#f0f5ff", border: "1px solid #d6e4ff" }}
                  bodyStyle={{ padding: "16px 20px" }}
                >
                  {/* Header: title left, percentage right */}
                  <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                    <Col>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 14 }}>
                        Vesting Status
                      </div>
                    </Col>
                    <Col style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: "#1890ff", lineHeight: 1.2 }}>
                        {balanceData.vestingComputations.vested_percentage}% vested
                      </div>
                      {balanceData.vestingComputations.vesting_rule_source === "customer" ? (
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#108ee9", letterSpacing: 1 }}>CUSTOM RATE</div>
                      ) : balanceData.vestingComputations.vesting_rule_source === "company" ? (
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#108ee9", letterSpacing: 1 }}>COMPANY RATE</div>
                      ) : null}
                    </Col>
                  </Row>

                  {/* Progress bar */}
                  <Progress
                    percent={balanceData.vestingComputations.vested_percentage}
                    showInfo={false}
                    strokeColor={{ "0%": "#108ee9", "100%": "#87d068" }}
                    style={{ marginBottom: 12 }}
                  />

                  {/* Footer: type tag + description left, date right */}
                  <Row justify="space-between" align="middle">
                    <Col>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Tag
                          color="blue"
                          style={{ fontWeight: 700, fontSize: 10, letterSpacing: 0.5 }}
                        >
                          {balanceData.vestingComputations.vesting_rule_source === "customer"
                            ? "CUSTOM VESTING"
                            : "STANDARD VESTING"}
                        </Tag>
                        {balanceData.vestingComputations.vesting_message && (
                          <span style={{ fontSize: 12, color: "#595959", display: "flex", alignItems: "center", gap: 4 }}>
                            <InfoCircleOutlined style={{ fontSize: 12 }} />
                            {balanceData.vestingComputations.vesting_message}
                          </span>
                        )}
                      </div>
                    </Col>
                    <Col style={{ textAlign: "right", fontSize: 12, color: "#8c8c8c" }}>
                      {balanceData.vestingComputations.vesting_start_date ? (
                        <>Effective from <strong>{moment(balanceData.vestingComputations.vesting_start_date).format("DD MMM YYYY")}</strong></>
                      ) : balanceData.vestingComputations.vesting_date_type ? (
                        <span style={{ color: "#ff4d4f" }}>
                          {balanceData.vestingComputations.vesting_date_type === "first_contribution_date"
                            ? "First Contribution Date not available"
                            : balanceData.vestingComputations.vesting_date_type === "date_of_employment"
                            ? "Date of Employment not available"
                            : `${formatSnakeCase(balanceData.vestingComputations.vesting_date_type)} not available`}
                        </span>
                      ) : null}
                      {balanceData.vestingComputations.vesting_rule_source === "none" ? (
                        <span style={{ marginLeft: 8 }}>No vesting rule available</span>
                      ) : balanceData.vestingComputations.years_in_vesting != null ? (
                        <span style={{ marginLeft: 8 }}>
                          · {balanceData.vestingComputations.years_in_vesting} yr{balanceData.vestingComputations.years_in_vesting !== 1 ? "s" : ""} in vesting
                        </span>
                      ) : null}
                    </Col>
                  </Row>
                </Card>
              )}

            {/* Pending Redemptions */}
            {renderPendingWithdrawals()}

            {/* Customer Transactions */}
            {renderCustomerTransactions()}
          </>
        )}
      </div>
    </Spin>
  );
};

export default CustomerBalanceBreakdown;
